import "./Bulletins.scss";
import {
  Breadcrumbs,
  Notification,
  Text,
  Heading,
} from "@netapp/bxp-design-system-react";
import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { Layout } from "@netapp/bxp-design-system-react/";
import { LangLink } from "../../utils/langNavigation";
import axios from "axios";
import { getContentPath, loadMarkdownContent } from "../../utils/contentLoader";

const Bulletins = () => {
  const { i18n, t } = useTranslation();
  const [frontMatter, setFrontMatter] = useState({});
  const [content, setContent] = useState("");
  const [bulletins, setBulletins] = useState([]);
  const [isLoadingBulletins, setIsLoadingBulletins] = useState(true);
  const [bulletinsError, setBulletinsError] = useState(null);

  // Only origin is needed here; origin never contains a path segment like /bulletins
  const baseUrl = window.location.origin;

  useEffect(() => {
    const loadContent = async () => {
      try {
        const filePath = getContentPath("/content/bulletins/_index", i18n.language);
        const { frontMatter: data, content: markdownContent } = await loadMarkdownContent(filePath);
        setFrontMatter(data);
        setContent(markdownContent);
      } catch (e) {
        // Optional: you could surface this with a Notification if needed
        // console.error("Error loading front matter", e);
      }
    };
    loadContent();
  }, [i18n.language]);

  useEffect(() => {
    const fetchBulletins = async () => {
      setIsLoadingBulletins(true);
      setBulletinsError(null);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/bulletins/`,
          {
            params: {
              lang: i18n.language
            }
          }
        );
        setBulletins(response?.data?.data || []);
      } catch (error) {
        setBulletinsError(error);
        // console.error("Error fetching bulletins:", error);
      } finally {
        setIsLoadingBulletins(false);
      }
    };

    fetchBulletins();
  }, [i18n.language]);

  // Locale-aware date formatting for better i18n mobile UX
  const formatDate = (isoDate) => {
    try {
      return new Date(isoDate).toLocaleDateString(i18n.language, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return new Date(isoDate).toLocaleDateString();
    }
  };

  const rssFeedUrl =
    i18n.language === "ja"
      ? `${baseUrl}/ja/bulletins/index.xml`
      : `${baseUrl}/bulletins/index.xml`;

  return (
    <div className="bulletins-page">
      <Layout.Page className="page-wrapper">
        <Layout.Content className="content-padding">
          <Layout.BreadcrumbsContainer className="breadcrumb-container">
            <Breadcrumbs>
              <LangLink to="/" className="breadcrumbs-link">
                {t("securityPortal.other", "Security Portal")}
              </LangLink>
              <div>{t("bulletins.other", "Bulletins")}</div>
            </Breadcrumbs>
          </Layout.BreadcrumbsContainer>

          <div className="title-and-content">
            <Text className="title">{frontMatter.longtitle}</Text>
            <Text className="title-description">
              <span>{frontMatter.slogan}</span>
              <span
                className="alert-link"
                onClick={() => window.open(rssFeedUrl, "_blank")}
                style={{ cursor: "pointer", textDecoration: "underline" }}
              >
                {t("subscribeRss.other", "Subscribe to the RSS feeds.")}
              </span>
            </Text>
          </div>

          {/* Optional error notification for API failures */}
          {bulletinsError && (
            <Notification
              variant="error"
              title={t("error.other", "Error")}
              description={t(
                "bulletinsFetchError.other",
                "We couldn’t load bulletins. Please try again."
              )}
              className="mb-16"
            />
          )}

          <div className="luci-table">
            <table className="luci-table__table" aria-label={t("bulletins.other", "Bulletins")}>
              <thead>
                <tr style={{ borderBottom: "1px solid #ddd" }}>
                  <th
                    className="luci-table__cell-header luci-text-level-6"
                    scope="col"
                  >
                    {t("bulletinsHeader.other", "NetApp Security Bulletins")}
                  </th>
                </tr>
              </thead>
              <tbody style={{ listStyle: "none" }}>
                {/* Loading skeletons / placeholders for mobile and desktop */}
                {isLoadingBulletins && (
                  <>
                    {[...Array(3)].map((_, idx) => (
                      <tr key={`skeleton-${idx}`}>
                        <td>
                          <div className="luci-long-form-text skeleton">
                            <div className="skeleton-title" />
                            <div className="skeleton-meta" />
                            <div className="skeleton-text" />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </>
                )}

                {!isLoadingBulletins &&
                  bulletins.map((bulletin) => (
                    <tr key={bulletin.bulletinid}>
                      <td>
                        <div className="luci-long-form-text">
                          <Heading as="h3">
                            {/* Use Link for SPA navigation; better on mobile */}
                            <LangLink
                              className="luci-link luci-link--block"
                              to={`/bulletins/${bulletin.bulletinid}`}
                              aria-label={`${bulletin.title}`}
                            >
                              {bulletin.title}
                            </LangLink>
                          </Heading>

                          <span className="luci-text-p--footnote mb-32">
                            {t("postedBy.other", "Posted by NetApp PSIRT")}/{" "}
                            {t("publishedDate.other", "Published Date")}:{" "}
                            {formatDate(bulletin.date)} /{" "}
                            {t("bulletinId.other", "Bulletin ID")}:{" "}
                            {bulletin.bulletinid}
                          </span>

                          {/* Clamp long text on mobile for readability */}
                          <Text className="bulletin-description">
                            {bulletin.description}
                          </Text>

                          <span>
                            <LangLink
                              className="luci-button luci-button--secondary luci-button--small luci-link"
                              to={`/bulletins/${bulletin.bulletinid}`}
                              aria-label={t("readMore.other", "Read more")}
                            >
                              {t("readMore.other", "Read more")} {"\u003e\u003e"}
                            </LangLink>
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}

                {!isLoadingBulletins && !bulletinsError && bulletins.length === 0 && (
                  <tr>
                    <td>
                      <div className="luci-long-form-text">
                        <Text>
                          {t("noBulletins.other", "No bulletins available at the moment.")}
                        </Text>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Layout.Content>
      </Layout.Page>
    </div>
  );
};

export default Bulletins;